import type {
  ConnectorParameterUndoEntry,
  ConnectorProjectParameterUndoEntry,
} from '../../types/parameterSetupUndo';

function optionalBindingKind(value: unknown): 'type' | 'instance' | undefined {
  return value === 'type' || value === 'instance' ? value : undefined;
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null
    ? value as Record<string, unknown>
    : null;
}

export function mapProjectUndoEntries(
  value: unknown,
): ConnectorProjectParameterUndoEntry[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const entries = value.flatMap((candidate) => {
    const item = readRecord(candidate);
    if (!item || typeof item.parameterName !== 'string' || item.parameterName.trim() === '') {
      return [];
    }

    const previousCategoryIds = Array.isArray(item.previousCategoryIds)
      ? item.previousCategoryIds.flatMap((categoryId) => {
          if (typeof categoryId === 'bigint') {
            return [categoryId.toString()];
          }
          if (typeof categoryId === 'number' && Number.isSafeInteger(categoryId)) {
            return [categoryId.toString()];
          }
          if (typeof categoryId === 'string' && /^-?\d+$/.test(categoryId)) {
            return [categoryId];
          }
          return [];
        })
      : [];
    const previousBindingKind = optionalBindingKind(item.previousBindingKind);
    return [{
      parameterName: item.parameterName.trim(),
      existedBefore: item.existedBefore === true,
      ...(previousBindingKind ? { previousBindingKind } : {}),
      previousCategoryIds,
      ...(typeof item.previousGroupTypeId === 'string' && item.previousGroupTypeId
        ? { previousGroupTypeId: item.previousGroupTypeId }
        : {}),
    }];
  });

  return entries.length > 0 ? entries : undefined;
}

export function toProjectUndoRequestEntry(entry: ConnectorProjectParameterUndoEntry) {
  return {
    parameterName: entry.parameterName,
    existedBefore: entry.existedBefore,
    previousBindingKind: entry.previousBindingKind ?? '',
    previousCategoryIds: entry.previousCategoryIds.map((categoryId) => BigInt(categoryId)),
    previousGroupTypeId: entry.previousGroupTypeId ?? '',
  };
}

export function mapFamilyUndoEntries(
  value: unknown,
): ConnectorParameterUndoEntry[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }

  const entries = value.flatMap((candidate) => {
    const item = readRecord(candidate);
    if (!item
      || typeof item.familyId !== 'string'
      || item.familyId.trim() === ''
      || typeof item.parameterName !== 'string'
      || item.parameterName.trim() === '') {
      return [];
    }
    const previousBindingKind = optionalBindingKind(item.previousBindingKind);
    return [{
      familyId: item.familyId.trim(),
      familyName: typeof item.familyName === 'string' ? item.familyName : '',
      parameterName: item.parameterName.trim(),
      existedBefore: item.existedBefore === true,
      ...(previousBindingKind ? { previousBindingKind } : {}),
    }];
  });

  return entries.length > 0 ? entries : undefined;
}
